import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Hook for syncing dynamic tabs with URL query parameters.
 *
 * @param tabs - The array of tab labels.
 * @param defaultTab - The default tab label.

 */
export function useTabParams(
    tabs: string[],
    defaultTab: string
): { currentIndex: number; currentTab: string; setTab: (index: number) => void } {
    const location = useLocation();
    const navigate = useNavigate();

    const currentTab: string = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (!tab) {
            return defaultTab;
        }
        return tabs.includes(tab) ? tab : defaultTab;
    }, [location.search, tabs, defaultTab]);

    // Ensure valid tab in URL on mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (!params.has('tab') || !tabs.includes(params.get('tab') as string)) {
            params.set('tab', defaultTab);
            navigate({ search: params.toString() }, { replace: true });
        }
    }, [navigate, location.search, tabs, defaultTab]);

    function setTab(index: number): void {
        const newTab = tabs[index] || defaultTab;
        const params = new URLSearchParams(location.search);
        params.set('tab', newTab);
        navigate({ search: params.toString() }, { replace: true });
    }

    return { currentTab, setTab, currentIndex: tabs.indexOf(currentTab) };
}
